import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  RefreshCw,
  ShieldCheck,
  Tag,
  Truck,
  CreditCard,
  Sparkles,
  ArrowRight,
  Check,
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import SEOHead from './SEOHead';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';

/**
 * Subscribe & Save, conversion-optimized landing page showcasing the
 * 10% monthly-subscription discount with a curated product grid.
 */
const SubscriptionsLandingPage = () => {
  const { currentLanguage } = useLanguage();
  const { formatPrice } = useCurrency();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`${API}/api/subscriptions/catalogue?limit=12`);
        const d = await r.json();
        setProducts(d.products || []);
      } catch {
        /* noop */
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [API]);

  const t = {
    en: {
      heroKicker: 'Subscribe & Save',
      heroTitle: 'Lock in 10% off, every month.',
      heroSubtitle:
        'Qualified research labs and licensed professionals get a flat 10% discount on every monthly subscription. Auto-renew, priority dispatch, no long-term lock-in.',
      heroCta: 'Browse subscription products',
      howItWorks: 'How the monthly subscription works',
      benefit1Title: 'Flat 10% off, always',
      benefit1: 'Discount is applied automatically to every billing cycle. No promo code needed.',
      benefit2Title: 'Priority dispatch',
      benefit2: 'Your recurring order ships on day 1 of the cycle, no queue, no stock-out surprises.',
      benefit3Title: 'Cancel anytime',
      benefit3: 'Manage or cancel self-service from the Stripe-hosted portal. No phone calls, no gotchas.',
      benefit4Title: 'Locked-in pricing',
      benefit4: 'Your rate stays fixed for 12 months, even if catalogue prices rise.',
      catalogueHeading: 'Popular subscription products',
      catalogueSubtitle: 'Research labs most often subscribe to these compounds.',
      saveBadge: 'SAVE 10%',
      orderBtn: 'View details',
      faqHeading: 'Frequently asked',
      faq1q: 'Am I locked in for any period?',
      faq1a: 'No. You can cancel or pause at any time from the self-service portal.',
      faq2q: 'What happens if a product is out of stock?',
      faq2a: 'Your billing pauses automatically; no charge until we ship the next batch.',
      faq3q: 'Can I skip a shipment?',
      faq3a: 'Yes, skip or reschedule the next delivery from your account area.',
      footerCta: 'Ready to subscribe?',
      footerCtaButton: 'Browse catalogue',
    },
    es: {
      heroKicker: 'Suscríbete y Ahorra',
      heroTitle: 'Asegura un 10% de descuento, cada mes.',
      heroSubtitle:
        'Laboratorios de investigación cualificados y profesionales autorizados obtienen un descuento fijo del 10% en cada suscripción mensual. Renovación automática, envío prioritario, sin permanencia.',
      heroCta: 'Ver productos disponibles',
      howItWorks: 'Cómo funciona la suscripción mensual',
      benefit1Title: '10% de descuento, siempre',
      benefit1: 'El descuento se aplica automáticamente en cada ciclo de facturación. Sin código.',
      benefit2Title: 'Envío prioritario',
      benefit2: 'Tu pedido recurrente se envía el día 1 del ciclo, sin cola, sin sorpresas de stock.',
      benefit3Title: 'Cancela cuando quieras',
      benefit3: 'Gestiona o cancela desde el portal de Stripe. Sin llamadas, sin trucos.',
      benefit4Title: 'Precio congelado',
      benefit4: 'Tu tarifa se mantiene 12 meses, incluso si suben los precios del catálogo.',
      catalogueHeading: 'Productos más suscritos',
      catalogueSubtitle: 'Los compuestos que más suelen suscribir los laboratorios.',
      saveBadge: 'AHORRA 10%',
      orderBtn: 'Ver detalles',
      faqHeading: 'Preguntas frecuentes',
      faq1q: '¿Hay permanencia?',
      faq1a: 'No. Puedes cancelar o pausar cuando quieras desde tu portal.',
      faq2q: '¿Qué pasa si un producto está agotado?',
      faq2a: 'Tu facturación se pausa automáticamente; no se cobra hasta que enviemos el siguiente lote.',
      faq3q: '¿Puedo saltarme un envío?',
      faq3a: 'Sí, puedes omitir o reprogramar la próxima entrega desde tu área de cliente.',
      footerCta: '¿Listo para suscribirte?',
      footerCtaButton: 'Ver catálogo',
    },
    ru: {
      heroKicker: 'Подписка и экономия',
      heroTitle: 'Фиксируйте 10% скидку каждый месяц.',
      heroSubtitle:
        'Сертифицированные исследовательские лаборатории и лицензированные специалисты получают фиксированную 10% скидку на каждую подписку. Автопродление, приоритетная отправка, никаких обязательств.',
      heroCta: 'Смотреть подписки',
      howItWorks: 'Как работает ежемесячная подписка',
      benefit1Title: 'Всегда 10% скидка',
      benefit1: 'Скидка применяется автоматически к каждому циклу. Промо-код не нужен.',
      benefit2Title: 'Приоритетная отправка',
      benefit2: 'Ваш заказ отправляется в 1-й день цикла, без очередей и нехватки запасов.',
      benefit3Title: 'Отмена в любой момент',
      benefit3: 'Управляйте или отменяйте через самообслуживание в Stripe. Без звонков.',
      benefit4Title: 'Фиксированная цена',
      benefit4: 'Ваш тариф зафиксирован на 12 месяцев, даже при росте цен каталога.',
      catalogueHeading: 'Популярные подписки',
      catalogueSubtitle: 'Соединения, которые чаще всего заказывают по подписке.',
      saveBadge: 'ЭКОНОМИЯ 10%',
      orderBtn: 'Подробнее',
      faqHeading: 'Частые вопросы',
      faq1q: 'Есть ли обязательства?',
      faq1a: 'Нет. Отменяйте или приостанавливайте в любой момент из личного кабинета.',
      faq2q: 'Что если товара нет в наличии?',
      faq2a: 'Биллинг автоматически приостанавливается; списание только перед следующей отправкой.',
      faq3q: 'Можно пропустить отправку?',
      faq3a: 'Да, пропускайте или переносите доставку из личного кабинета.',
      footerCta: 'Готовы оформить подписку?',
      footerCtaButton: 'Смотреть каталог',
    },
    de: {
      heroKicker: 'Abonnieren & Sparen',
      heroTitle: 'Sichern Sie sich monatlich 10 % Rabatt.',
      heroSubtitle: 'Qualifizierte Forschungslabore und lizenzierte Fachleute erhalten einen festen Rabatt von 10 % auf jedes monatliche Abonnement. Automatische Verlängerung, prioritärer Versand, keine langfristige Bindung.',
      heroCta: 'Abonnement-Produkte durchsuchen',
      howItWorks: 'So funktioniert das monatliche Abonnement',
      benefit1Title: 'Immer 10 % Rabatt',
      benefit1: 'Der Rabatt wird in jedem Abrechnungszyklus automatisch angewendet. Kein Code erforderlich.',
      benefit2Title: 'Prioritärer Versand',
      benefit2: 'Ihre wiederkehrende Bestellung wird am 1. Tag des Zyklus versandt, keine Warteschlange, keine Stockout-Überraschungen.',
      benefit3Title: 'Jederzeit kündbar',
      benefit3: 'Selbstständig im Stripe-Portal verwalten oder kündigen. Keine Anrufe, keine Tricks.',
      benefit4Title: 'Festgeschriebener Preis',
      benefit4: 'Ihr Preis bleibt 12 Monate fest, auch wenn die Katalogpreise steigen.',
      catalogueHeading: 'Beliebte Abonnement-Produkte',
      catalogueSubtitle: 'Diese Verbindungen abonnieren Forschungslabore am häufigsten.',
      saveBadge: '10 % SPAREN',
      orderBtn: 'Details ansehen',
      faqHeading: 'Häufige Fragen',
      faq1q: 'Bin ich für einen bestimmten Zeitraum gebunden?',
      faq1a: 'Nein. Sie können jederzeit über das Self-Service-Portal kündigen oder pausieren.',
      faq2q: 'Was passiert, wenn ein Produkt ausverkauft ist?',
      faq2a: 'Ihre Abrechnung pausiert automatisch; keine Belastung, bis wir die nächste Charge versenden.',
      faq3q: 'Kann ich eine Lieferung überspringen?',
      faq3a: 'Ja, überspringen oder verschieben Sie die nächste Lieferung über Ihren Kontobereich.',
      footerCta: 'Bereit zum Abonnieren?',
      footerCtaButton: 'Katalog durchsuchen',
    },
    fr: {
      heroKicker: 'Abonnez-vous et économisez',
      heroTitle: 'Verrouillez 10 % de réduction, chaque mois.',
      heroSubtitle: 'Les laboratoires de recherche qualifiés et les professionnels agréés bénéficient d’une remise fixe de 10 % sur chaque abonnement mensuel. Renouvellement automatique, expédition prioritaire, sans engagement.',
      heroCta: 'Parcourir les produits d’abonnement',
      howItWorks: 'Comment fonctionne l’abonnement mensuel',
      benefit1Title: '10 % de réduction, toujours',
      benefit1: 'La remise est appliquée automatiquement à chaque cycle. Aucun code promo nécessaire.',
      benefit2Title: 'Expédition prioritaire',
      benefit2: 'Votre commande récurrente est expédiée le 1er jour du cycle, sans file d’attente, sans rupture.',
      benefit3Title: 'Annulez à tout moment',
      benefit3: 'Gérez ou annulez en libre-service depuis le portail Stripe. Aucun appel, aucune embûche.',
      benefit4Title: 'Tarif bloqué',
      benefit4: 'Votre tarif reste fixe pendant 12 mois, même si les prix du catalogue augmentent.',
      catalogueHeading: 'Produits d’abonnement populaires',
      catalogueSubtitle: 'Les composés que les laboratoires de recherche abonnent le plus souvent.',
      saveBadge: 'ÉCONOMISEZ 10 %',
      orderBtn: 'Voir les détails',
      faqHeading: 'Questions fréquentes',
      faq1q: 'Suis-je engagé pour une durée ?',
      faq1a: 'Non. Vous pouvez annuler ou suspendre à tout moment depuis le portail libre-service.',
      faq2q: 'Que se passe-t-il si un produit est en rupture ?',
      faq2a: 'Votre facturation se met en pause automatiquement ; aucun débit jusqu’à l’expédition suivante.',
      faq3q: 'Puis-je sauter une livraison ?',
      faq3a: 'Oui, sautez ou reprogrammez la prochaine livraison depuis votre espace.',
      footerCta: 'Prêt à vous abonner ?',
      footerCtaButton: 'Parcourir le catalogue',
    },
    it: {
      heroKicker: 'Abbonati e Risparmia',
      heroTitle: 'Blocca il 10% di sconto, ogni mese.',
      heroSubtitle: 'I laboratori di ricerca qualificati e i professionisti autorizzati ottengono uno sconto fisso del 10% su ogni abbonamento mensile. Rinnovo automatico, spedizione prioritaria, nessun vincolo a lungo termine.',
      heroCta: 'Sfoglia i prodotti in abbonamento',
      howItWorks: 'Come funziona l’abbonamento mensile',
      benefit1Title: 'Sconto fisso del 10%, sempre',
      benefit1: 'Lo sconto è applicato automaticamente a ogni ciclo di fatturazione. Nessun codice necessario.',
      benefit2Title: 'Spedizione prioritaria',
      benefit2: 'Il tuo ordine ricorrente parte il 1° giorno del ciclo, niente code, niente sorprese di stock.',
      benefit3Title: 'Annulla in qualsiasi momento',
      benefit3: 'Gestisci o annulla in self-service dal portale Stripe. Niente telefonate.',
      benefit4Title: 'Prezzo bloccato',
      benefit4: 'La tua tariffa resta fissa per 12 mesi, anche se i prezzi a catalogo aumentano.',
      catalogueHeading: 'Prodotti più abbonati',
      catalogueSubtitle: 'I composti che i laboratori abbonano più spesso.',
      saveBadge: 'RISPARMIA 10%',
      orderBtn: 'Vedi dettagli',
      faqHeading: 'Domande frequenti',
      faq1q: 'Sono vincolato per un periodo?',
      faq1a: 'No. Puoi annullare o sospendere in qualsiasi momento dal portale self-service.',
      faq2q: 'Cosa succede se un prodotto è esaurito?',
      faq2a: 'La fatturazione si sospende automaticamente; nessun addebito fino alla prossima spedizione.',
      faq3q: 'Posso saltare una spedizione?',
      faq3a: 'Sì, salta o riprogramma la prossima consegna dall’area cliente.',
      footerCta: 'Pronto ad abbonarti?',
      footerCtaButton: 'Sfoglia il catalogo',
    },
    pl: {
      heroKicker: 'Subskrybuj i Oszczędzaj',
      heroTitle: 'Zablokuj 10 % zniżki co miesiąc.',
      heroSubtitle: 'Wykwalifikowane laboratoria badawcze i licencjonowani specjaliści otrzymują stałą 10% zniżkę na każdą miesięczną subskrypcję. Automatyczne odnawianie, priorytetowa wysyłka, brak długoterminowych zobowiązań.',
      heroCta: 'Przeglądaj produkty subskrypcyjne',
      howItWorks: 'Jak działa miesięczna subskrypcja',
      benefit1Title: 'Stałe 10 % zniżki, zawsze',
      benefit1: 'Zniżka jest stosowana automatycznie do każdego cyklu rozliczeniowego. Bez kodu promocyjnego.',
      benefit2Title: 'Priorytetowa wysyłka',
      benefit2: 'Twoje cykliczne zamówienie wysyłane jest 1. dnia cyklu, bez kolejek i niespodzianek z brakiem towaru.',
      benefit3Title: 'Anuluj w dowolnym momencie',
      benefit3: 'Zarządzaj lub anuluj samodzielnie z portalu Stripe. Bez telefonów, bez podstępów.',
      benefit4Title: 'Zablokowana cena',
      benefit4: 'Twoja stawka pozostaje stała przez 12 miesięcy, nawet jeśli ceny w katalogu wzrosną.',
      catalogueHeading: 'Popularne produkty subskrypcyjne',
      catalogueSubtitle: 'Związki, które laboratoria badawcze subskrybują najczęściej.',
      saveBadge: 'OSZCZĘDŹ 10 %',
      orderBtn: 'Zobacz szczegóły',
      faqHeading: 'Najczęściej zadawane pytania',
      faq1q: 'Czy jestem związany umową na jakiś okres?',
      faq1a: 'Nie. Możesz anulować lub wstrzymać w dowolnym momencie z portalu samoobsługowego.',
      faq2q: 'Co się stanie, jeśli produkt jest niedostępny?',
      faq2a: 'Twoje rozliczenie zostaje automatycznie wstrzymane; brak obciążenia do następnej wysyłki.',
      faq3q: 'Czy mogę pominąć wysyłkę?',
      faq3a: 'Tak, pomiń lub przełóż następną dostawę ze swojego konta.',
      footerCta: 'Gotowy do subskrypcji?',
      footerCtaButton: 'Przeglądaj katalog',
    },
    nl: {
      heroKicker: 'Abonneer & Bespaar',
      heroTitle: 'Vergrendel 10% korting, elke maand.',
      heroSubtitle: 'Gekwalificeerde onderzoekslaboratoria en gelicentieerde professionals krijgen een vaste 10% korting op elk maandelijks abonnement. Automatische verlenging, prioritaire verzending, geen langetermijnverplichting.',
      heroCta: 'Bekijk abonnementsproducten',
      howItWorks: 'Hoe het maandelijkse abonnement werkt',
      benefit1Title: 'Vaste 10% korting, altijd',
      benefit1: 'De korting wordt automatisch toegepast op elke facturatiecyclus. Geen actiecode nodig.',
      benefit2Title: 'Prioritaire verzending',
      benefit2: 'Uw terugkerende bestelling wordt op dag 1 van de cyclus verzonden, geen wachtrij, geen voorraadverrassingen.',
      benefit3Title: 'Op elk moment opzegbaar',
      benefit3: 'Beheer of zeg zelf op via het Stripe-portaal. Geen telefoontjes, geen verrassingen.',
      benefit4Title: 'Vaste prijs',
      benefit4: 'Uw tarief blijft 12 maanden vast, ook als de catalogusprijzen stijgen.',
      catalogueHeading: 'Populaire abonnementsproducten',
      catalogueSubtitle: 'De verbindingen waarop onderzoekslaboratoria het vaakst een abonnement nemen.',
      saveBadge: 'BESPAAR 10%',
      orderBtn: 'Bekijk details',
      faqHeading: 'Veelgestelde vragen',
      faq1q: 'Ben ik gebonden aan een termijn?',
      faq1a: 'Nee. U kunt op elk moment opzeggen of pauzeren vanuit het self-service portaal.',
      faq2q: 'Wat gebeurt er als een product niet op voorraad is?',
      faq2a: 'Uw facturatie wordt automatisch gepauzeerd; geen kosten totdat we de volgende partij verzenden.',
      faq3q: 'Kan ik een zending overslaan?',
      faq3a: 'Ja, sla over of verplaats de volgende levering vanuit uw account.',
      footerCta: 'Klaar om te abonneren?',
      footerCtaButton: 'Bekijk catalogus',
    },
    pt: {
      heroKicker: 'Assine e Economize',
      heroTitle: 'Garanta 10% de desconto todo mês.',
      heroSubtitle: 'Laboratórios de pesquisa qualificados e profissionais licenciados recebem um desconto fixo de 10% em cada assinatura mensal. Renovação automática, envio prioritário, sem compromisso de longo prazo.',
      heroCta: 'Ver produtos de assinatura',
      howItWorks: 'Como funciona a assinatura mensal',
      benefit1Title: '10% de desconto fixo, sempre',
      benefit1: 'O desconto é aplicado automaticamente em cada ciclo de cobrança. Sem código promocional.',
      benefit2Title: 'Envio prioritário',
      benefit2: 'Seu pedido recorrente é enviado no 1º dia do ciclo, sem fila, sem surpresas de estoque.',
      benefit3Title: 'Cancele a qualquer momento',
      benefit3: 'Gerencie ou cancele pelo portal autosserviço do Stripe. Sem ligações, sem pegadinhas.',
      benefit4Title: 'Preço travado',
      benefit4: 'Sua tarifa permanece fixa por 12 meses, mesmo se os preços do catálogo subirem.',
      catalogueHeading: 'Produtos de assinatura populares',
      catalogueSubtitle: 'Os compostos que os laboratórios de pesquisa mais assinam.',
      saveBadge: 'ECONOMIZE 10%',
      orderBtn: 'Ver detalhes',
      faqHeading: 'Perguntas frequentes',
      faq1q: 'Estou comprometido por algum período?',
      faq1a: 'Não. Você pode cancelar ou pausar a qualquer momento pelo portal autosserviço.',
      faq2q: 'O que acontece se um produto estiver esgotado?',
      faq2a: 'Sua cobrança é pausada automaticamente; sem cobrança até enviarmos o próximo lote.',
      faq3q: 'Posso pular um envio?',
      faq3a: 'Sim, pule ou reagende a próxima entrega na sua área de cliente.',
      footerCta: 'Pronto para assinar?',
      footerCtaButton: 'Ver catálogo',
    },
    au: {
      heroKicker: 'Subscribe & Save',
      heroTitle: 'Lock in 10% off, every month.',
      heroSubtitle: 'Qualified research labs and licensed professionals get a flat 10% discount on every monthly subscription. Auto-renew, priority dispatch, no long-term lock-in.',
      heroCta: 'Browse subscription products',
      howItWorks: 'How the monthly subscription works',
      benefit1Title: 'Flat 10% off, always',
      benefit1: 'Discount is applied automatically to every billing cycle. No promo code needed.',
      benefit2Title: 'Priority dispatch',
      benefit2: 'Your recurring order ships on day 1 of the cycle, no queue, no stock-out surprises.',
      benefit3Title: 'Cancel anytime',
      benefit3: 'Manage or cancel self-service from the Stripe-hosted portal. No phone calls, no gotchas.',
      benefit4Title: 'Locked-in pricing',
      benefit4: 'Your rate stays fixed for 12 months, even if catalogue prices rise.',
      catalogueHeading: 'Popular subscription products',
      catalogueSubtitle: 'Research labs most often subscribe to these compounds.',
      saveBadge: 'SAVE 10%',
      orderBtn: 'View details',
      faqHeading: 'Frequently asked',
      faq1q: 'Am I locked in for any period?',
      faq1a: 'No. You can cancel or pause at any time from the self-service portal.',
      faq2q: 'What happens if a product is out of stock?',
      faq2a: 'Your billing pauses automatically; no charge until we ship the next batch.',
      faq3q: 'Can I skip a shipment?',
      faq3a: 'Yes, skip or reschedule the next delivery from your account area.',
      footerCta: 'Ready to subscribe?',
      footerCtaButton: 'Browse catalogue',
    },
  }[currentLanguage] || {
      heroKicker: 'Subscribe & Save',
      heroTitle: 'Lock in 10% off, every month.',
      heroSubtitle: 'Qualified research labs and licensed professionals get a flat 10% discount on every monthly subscription. Auto-renew, priority dispatch, no long-term lock-in.',
      heroCta: 'Browse subscription products',
      howItWorks: 'How the monthly subscription works',
      benefit1Title: 'Flat 10% off, always',
      benefit1: 'Discount is applied automatically to every billing cycle. No promo code needed.',
      benefit2Title: 'Priority dispatch',
      benefit2: 'Your recurring order ships on day 1 of the cycle, no queue, no stock-out surprises.',
      benefit3Title: 'Cancel anytime',
      benefit3: 'Manage or cancel self-service from the Stripe-hosted portal. No phone calls, no gotchas.',
      benefit4Title: 'Locked-in pricing',
      benefit4: 'Your rate stays fixed for 12 months, even if catalogue prices rise.',
      catalogueHeading: 'Popular subscription products',
      catalogueSubtitle: 'Research labs most often subscribe to these compounds.',
      saveBadge: 'SAVE 10%',
      orderBtn: 'View details',
      faqHeading: 'Frequently asked',
      faq1q: 'Am I locked in for any period?',
      faq1a: 'No. You can cancel or pause at any time from the self-service portal.',
      faq2q: 'What happens if a product is out of stock?',
      faq2a: 'Your billing pauses automatically; no charge until we ship the next batch.',
      faq3q: 'Can I skip a shipment?',
      faq3a: 'Yes, skip or reschedule the next delivery from your account area.',
      footerCta: 'Ready to subscribe?',
      footerCtaButton: 'Browse catalogue',
  };

  const benefits = [
    { icon: Tag, title: t.benefit1Title, body: t.benefit1 },
    { icon: Truck, title: t.benefit2Title, body: t.benefit2 },
    { icon: RefreshCw, title: t.benefit3Title, body: t.benefit3 },
    { icon: ShieldCheck, title: t.benefit4Title, body: t.benefit4 },
  ];

  const pickName = (nameObj) => {
    if (!nameObj) return '';
    if (typeof nameObj === 'string') return nameObj;
    return nameObj[currentLanguage] || nameObj.en || Object.values(nameObj)[0] || '';
  };

  return (
    <div className="min-h-screen bg-capsule-bg" data-testid="subscriptions-landing">
      <SEOHead page="subscriptions" />
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0b1220] via-[#0f1f2d] to-[#0D3A3A] py-20 md:py-28">
        <div className="absolute inset-0 opacity-20 pointer-events-none" aria-hidden="true">
          <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-capsule-coral/30 blur-3xl" />
          <div className="absolute top-40 right-10 w-96 h-96 rounded-full bg-capsule-teal/30 blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-capsule-coral/20 to-capsule-salmon/20 border border-capsule-coral/40 text-capsule-coral text-xs uppercase tracking-widest font-bold mb-6">
            <Sparkles className="w-3.5 h-3.5" /> {t.heroKicker}
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight" data-testid="landing-hero-title">
            {t.heroTitle}
          </h1>
          <p className="text-lg md:text-xl text-white/70 max-w-3xl mx-auto mb-9 leading-relaxed">
            {t.heroSubtitle}
          </p>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-capsule-coral to-capsule-salmon text-white font-semibold shadow-xl hover:opacity-90 transition"
            data-testid="landing-hero-cta"
          >
            {t.heroCta}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl md:text-4xl font-bold text-center text-gray-900 mb-3">
            {t.howItWorks}
          </h2>
          <div className="w-16 h-1 bg-gradient-to-r from-capsule-coral to-capsule-salmon mx-auto mb-12 rounded-full" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {benefits.map((b, i) => (
              <div
                key={i}
                className="glass-card rounded-2xl p-6 text-left hover:shadow-lg hover:-translate-y-1 transition-all"
                data-testid={`benefit-card-${i}`}
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-capsule-teal to-capsule-aqua flex items-center justify-center mb-3">
                  <b.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-bold text-gray-900 mb-1">{b.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product grid */}
      <section className="py-16 md:py-20 bg-capsule-bg-light">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl md:text-4xl font-bold text-center text-gray-900 mb-3">
            {t.catalogueHeading}
          </h2>
          <p className="text-center text-gray-600 mb-10">{t.catalogueSubtitle}</p>

          {loading ? (
            <div className="text-center text-gray-500">Loading...</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {products.map((p) => (
                <Link
                  key={p.id}
                  to={`/product/${p.id}`}
                  className="group relative glass-card rounded-2xl p-4 hover:shadow-xl hover:-translate-y-1 transition-all"
                  data-testid={`sub-product-${p.id}`}
                >
                  <span
                    className="absolute -top-2 -right-2 bg-gradient-to-r from-capsule-coral to-capsule-salmon text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full shadow-md flex items-center gap-1 z-10"
                  >
                    <Tag className="w-3 h-3" /> {t.saveBadge}
                  </span>

                  <div className="aspect-square rounded-xl bg-gradient-to-br from-white to-slate-50 p-3 mb-3 flex items-center justify-center overflow-hidden">
                    <img
                      src={p.image_url}
                      alt={pickName(p.name)}
                      loading="lazy"
                      className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform"
                    />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2 line-clamp-2">
                    {pickName(p.name)}
                  </h3>

                  {p.benefits_preview?.length > 0 && (
                    <ul className="mb-3 space-y-0.5">
                      {p.benefits_preview.slice(0, 2).map((b, i) => (
                        <li key={i} className="flex items-start gap-1 text-xs text-gray-600">
                          <Check className="w-3 h-3 text-capsule-teal flex-shrink-0 mt-0.5" />
                          <span className="line-clamp-1">{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="flex items-baseline justify-between pt-2 border-t border-gray-100">
                    <div>
                      <div className="text-lg font-bold text-capsule-coral">
                        {formatPrice(p.subscription_price_eur)}
                        <span className="text-xs font-normal text-gray-500">/mo</span>
                      </div>
                      <div className="text-[11px] text-gray-400 line-through">
                        {formatPrice(p.one_time_price_eur)}
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-capsule-teal group-hover:translate-x-1 transition" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl md:text-4xl font-bold text-center text-gray-900 mb-10">
            {t.faqHeading}
          </h2>
          <div className="space-y-4">
            {[[t.faq1q, t.faq1a], [t.faq2q, t.faq2a], [t.faq3q, t.faq3a]].map(
              ([q, a], i) => (
                <details
                  key={i}
                  className="glass-card rounded-xl p-5 group"
                  data-testid={`landing-faq-${i}`}
                >
                  <summary className="cursor-pointer font-semibold text-gray-900 flex justify-between items-center">
                    {q}
                    <span className="text-capsule-teal group-open:rotate-180 transition-transform">
                      ▾
                    </span>
                  </summary>
                  <p className="mt-3 text-sm text-gray-600 leading-relaxed">{a}</p>
                </details>
              )
            )}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-capsule-teal via-capsule-aqua to-capsule-coral">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <CreditCard className="w-12 h-12 text-white/80 mx-auto mb-5" />
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            {t.footerCta}
          </h2>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-capsule-teal font-bold shadow-xl hover:scale-105 transition-transform"
            data-testid="landing-footer-cta"
          >
            {t.footerCtaButton}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SubscriptionsLandingPage;
